import { useState, useEffect } from 'react';
import { HashRouter, useLocation } from 'react-router-dom';
import { Flex } from '@radix-ui/themes';

import { VaultHeader } from './VaultHeader.jsx';
import { VaultListView } from './VaultListView.jsx';
import { CategoryView } from './CategoryView/CategoryView.jsx';
import LoadingComponent from '../../../shared/LoadingComponent';
import { useGetVaultFiles } from '../data';
import { useMultipleFileUpload } from '../useMultipleFileUpload';
import useLocalStorage from '../../../lib/hooks/useLocalStorage';
import { useURLParam } from '../../../lib/hooks/useURLParam';
import { VIEW_TYPES } from '../constants';

const VaultContent = ({ organizationData }) => {
  const location = useLocation();

  const [getViewFromURL, setViewInURL] = useURLParam(
    'view',
    Object.values(VIEW_TYPES),
  );

  const [storedView, setStoredView] = useLocalStorage(
    'vaultActiveView',
    VIEW_TYPES.LIST,
  );

  const [activeView, setActiveView] = useState(() => {
    const viewFromURL = getViewFromURL();
    if (viewFromURL) {
      setStoredView(viewFromURL);
      return viewFromURL;
    }
    return storedView;
  });

  useEffect(() => {
    const viewFromURL = getViewFromURL();
    if (viewFromURL && viewFromURL !== activeView) {
      setActiveView(viewFromURL);
      setStoredView(viewFromURL);
    }
  }, [location.search, activeView, setStoredView, getViewFromURL]);

  useEffect(() => {
    window.Airbo.Utils.ping('Vault - Viewed page', { viewType: activeView });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleViewToggle = viewType => {
    setActiveView(viewType);
    setStoredView(viewType);
    setViewInURL(viewType);
    if (viewType === VIEW_TYPES.LIST) setSelectedCategory(null);

    window.Airbo.Utils.ping('Vault - Toggled view', { viewType });
  };

  const { uploadingFiles, uploadFiles } = useMultipleFileUpload();

  const hasActiveUploads = uploadingFiles.some(
    file => file.status === 'uploading',
  );

  useEffect(() => {
    const handleBeforeUnload = event => {
      if (hasActiveUploads) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasActiveUploads]);

  const {
    data: baseData = { categories: [], categoryCounts: {}, vendors: [] },
    isLoading,
  } = useGetVaultFiles({
    page: 1,
    itemsPerPage: 1,
    showAll: false,
  });

  const { categories = [], categoryCounts = {}, vendors = [] } = baseData;

  const [selectedCategory, setSelectedCategory] = useState(null);

  return (
    <Flex m="5" direction="column" gap="5">
      <VaultHeader
        activeView={activeView}
        onViewToggle={handleViewToggle}
        organizationId={organizationData.id}
        uploadFiles={uploadFiles}
        categories={categories}
        vendors={vendors}
        selectedCategory={selectedCategory}
        onClearCategory={() => setSelectedCategory(null)}
      />
      {isLoading ? (
        <LoadingComponent />
      ) : activeView === VIEW_TYPES.LIST ? (
        <VaultListView
          organizationData={organizationData}
          uploadingFiles={uploadingFiles}
          categories={categories}
          vendors={vendors}
        />
      ) : (
        <CategoryView
          categories={categories}
          categoryCounts={categoryCounts}
          vendors={vendors}
          uploadingFiles={uploadingFiles}
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
        />
      )}
    </Flex>
  );
};

export const Vault = ({ organizationData }) => {
  return (
    <HashRouter hashType="noslash">
      <VaultContent organizationData={organizationData} />
    </HashRouter>
  );
};
